import { useCallback, useEffect, useRef, useState } from 'react';

import { CarouselSkeleton } from '@jane/bloom/components/skeletonLoaders/carouselSkeleton';
import { useIsNavigatingAway } from '@jane/bloom/hooks/navigation/useIsNavigatingAway';
import { usePinStore } from '@jane/bloom/hooks/storeSwitcher/usePinStore';
import type { CarouselBanner } from '@jane/bloom/types/schemas/roots/carouselBanner';

import { CarouselSwiper } from './carouselSwiper/carouselSwiper';

interface CarouselProps {
  items: CarouselBanner;
  stockPhoto?: boolean;
  storeName?: string;
  variant?: 'brand' | 'product';
  withLightBox?: boolean;
}

const AUTO_SLIDE_INTERVAL = 7000;

export const Carousel = ({ items, storeName, variant }: CarouselProps) => {
  const { pinning: manuallyPinning } = usePinStore();
  const isNavigatingAway = useIsNavigatingAway();
  const [activeIndex, setActiveIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldShowSkeleton = manuallyPinning || isNavigatingAway;

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % items.length);
    }, AUTO_SLIDE_INTERVAL);
  }, [items.length]);

  const updateIndex = (newIndex: number) => {
    setActiveIndex(newIndex);
    startAutoSlide(); // reset interval when index is manually updated
  };

  useEffect(() => {
    startAutoSlide();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startAutoSlide]);

  if (shouldShowSkeleton) {
    return <CarouselSkeleton mode="carousel" />;
  }

  return (
    <CarouselSwiper
      items={items}
      activeIndex={activeIndex}
      updateIndex={updateIndex}
      variant={variant}
      storeName={storeName}
    />
  );
};
